/**
 * Contact Work letter-packet generate, mail-batch, and print/download actions.
 */
import { formatInteger } from "./format.js";
import { escapeHtml } from "./dashboard-html.js";
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import type { DashboardActionHost } from "./dashboard-referenced-deed-actions.js";
import {
  leadFactoryContactLetterPacketBatchUrl,
  leadFactoryContactLetterPacketMailManifestUrl,
  leadFactoryContactLetterPacketReturnedMailBatchUrl,
  leadFactoryContactLetterPacketSendBatchUrl,
  leadFactoryContactMailBatchVendorHandoffUrl
} from "./dashboard-urls.js";

function cssEscape(value: string): string {
  if (globalThis.CSS?.escape) return globalThis.CSS.escape(value);
  return value.replaceAll("\\", "\\\\").replaceAll('"', '\\"');
}

export async function downloadAuthenticatedFile(
  url: string,
  filename: string,
  mimeType: string,
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  statusElement.textContent = `Preparing ${filename}...`;
  try {
    const response = await authenticatedFetch(url);
    if (!response.ok) throw new Error(`Download failed with ${response.status}`);
    const blob = new Blob([await response.text()], { type: mimeType });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(objectUrl);
    statusElement.textContent = `Downloaded ${filename}.`;
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Download failed.";
  }
}

export async function printAuthenticatedLetterPacket(url: string, packetId: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  statusElement.textContent = `Preparing ${packetId} for print...`;
  try {
    const response = await authenticatedFetch(url);
    if (!response.ok) throw new Error(`Packet print failed with ${response.status}`);
    const text = await response.text();
    const printWindow = window.open("", "_blank", "noopener,noreferrer");
    if (!printWindow) throw new Error("Packet print window was blocked.");
    printWindow.document.write(`<!doctype html><title>${escapeHtml(packetId)}</title><pre>${escapeHtml(text)}</pre>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    statusElement.textContent = `Opened ${packetId} for print.`;
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Packet print failed.";
  }
}

export async function submitContactLetterPacket(
  leadId: string,
  taskId: string,
  payload: { senderName?: string; note?: string },
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  statusElement.textContent = `Generating letter packet for ${leadId}...`;
  try {
    const response = await authenticatedFetch(`/dashboard/lead-factory/leads/${encodeURIComponent(leadId)}/letter-packet`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...payload, taskId })
    });
    const body = (await response.json()) as { error?: string; id?: string; status?: string; artifactUri?: string; missingFields?: string[] };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    const missing = Array.isArray(body.missingFields) && body.missingFields.length > 0 ? ` Missing: ${body.missingFields.join(", ")}.` : "";
    statusElement.textContent = `${leadId}: letter packet ${body.id ?? ""} ${body.status ?? "generated"} at ${body.artifactUri ?? "artifact"}.${missing}`;
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Letter packet generation failed.";
  }
}

export async function submitContactLetterPacketBatch(
  tasks: Array<{ propertyLeadId: string; taskId: string }>,
  payload: { senderName?: string; note?: string },
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  if (tasks.length === 0) {
    statusElement.textContent = "Mail batch cancelled; no visible letter tasks are linked.";
    return;
  }
  statusElement.textContent = `Generating ${formatInteger(tasks.length)} visible letter packets...`;
  try {
    const response = await authenticatedFetch(leadFactoryContactLetterPacketBatchUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...payload, tasks })
    });
    const body = (await response.json()) as {
      error?: string;
      batchId?: string;
      generatedPacketCount?: number;
      failureCount?: number;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `Mail batch ${body.batchId ?? ""}: ${formatInteger(body.generatedPacketCount ?? 0)} packets generated, ${formatInteger(body.failureCount ?? 0)} failures. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Mail batch generation failed.";
  }
}

export async function submitContactLetterPacketMailManifest(
  packetIds: string[],
  payload: { note?: string },
  statusElement: HTMLElement,
  panel: HTMLElement | undefined,
  actions: DashboardActionHost
): Promise<void> {
  if (packetIds.length === 0) {
    statusElement.textContent = "Mail manifest cancelled; no visible ready-to-print packets are linked.";
    return;
  }
  statusElement.textContent = `Creating manifest for ${formatInteger(packetIds.length)} visible letter packets...`;
  try {
    const response = await authenticatedFetch(leadFactoryContactLetterPacketMailManifestUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...payload, packetIds })
    });
    const body = (await response.json()) as {
      error?: string;
      batchId?: string;
      manifestPacketCount?: number;
      failureCount?: number;
      artifactUrl?: string;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `Mail manifest ${body.batchId ?? ""}: ${formatInteger(body.manifestPacketCount ?? 0)} packets listed, ${formatInteger(body.failureCount ?? 0)} failures.`;
    if (panel && body.batchId) {
      panel.dataset.contactMailBatchId = body.batchId;
      const handoffButton = panel.querySelector<HTMLButtonElement>("[data-contact-letter-batch-handoff]");
      if (handoffButton) handoffButton.disabled = false;
    }
    if (body.artifactUrl && body.batchId) {
      await downloadAuthenticatedFile(body.artifactUrl, `${body.batchId}.md`, "text/markdown", statusElement, actions);
    }
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Mail manifest creation failed.";
  }
}

export async function submitContactMailBatchVendorHandoff(
  batchId: string,
  payload: { handoffType?: string; vendorName?: string; note?: string },
  statusElement: HTMLElement,
  actions: DashboardActionHost
): Promise<void> {
  if (!batchId) {
    statusElement.textContent = "Vendor handoff cancelled; create a mail manifest first.";
    return;
  }
  statusElement.textContent = `Creating vendor handoff for ${batchId}...`;
  try {
    const response = await authenticatedFetch(leadFactoryContactMailBatchVendorHandoffUrl(batchId), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    });
    const body = (await response.json()) as {
      error?: string;
      handoffId?: string;
      packetCount?: number;
      artifactUrl?: string;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `Vendor handoff ${body.handoffId ?? ""}: ${formatInteger(body.packetCount ?? 0)} packets ready.`;
    if (body.artifactUrl && body.handoffId) {
      await downloadAuthenticatedFile(body.artifactUrl, `${body.handoffId}.md`, "text/markdown", statusElement, actions);
    }
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Vendor handoff creation failed.";
  }
}

export async function submitContactLetterPacketSendBatch(
  packetIds: string[],
  payload: { note?: string },
  statusElement: HTMLElement,
  lane: HTMLElement | undefined,
  panel: HTMLElement | undefined,
  actions: DashboardActionHost
): Promise<void> {
  if (packetIds.length === 0) {
    statusElement.textContent = "Mail send batch cancelled; no visible ready-to-print packets are linked.";
    return;
  }
  statusElement.textContent = `Recording ${formatInteger(packetIds.length)} visible letter packets as sent...`;
  try {
    const response = await authenticatedFetch(leadFactoryContactLetterPacketSendBatchUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...payload, packetIds })
    });
    const body = (await response.json()) as {
      error?: string;
      batchId?: string;
      sentPacketCount?: number;
      failureCount?: number;
      sent?: Array<{ packetId?: string }>;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    for (const packet of body.sent ?? []) {
      const packetId = packet.packetId?.trim();
      if (!packetId) continue;
      const row = lane?.querySelector<HTMLElement>(`[data-contact-letter-packet-row][data-letter-packet-id="${cssEscape(packetId)}"]`);
      if (row) row.dataset.letterPacketStatus = "sent";
    }
    const returnedButton = panel?.querySelector<HTMLButtonElement>("[data-contact-letter-batch-returned]");
    if (returnedButton && (body.sentPacketCount ?? 0) > 0) returnedButton.disabled = false;
    statusElement.textContent = `Mail send batch ${body.batchId ?? ""}: ${formatInteger(body.sentPacketCount ?? 0)} packets sent, ${formatInteger(body.failureCount ?? 0)} failures.`;
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Mail send batch failed.";
  }
}

export async function submitContactLetterPacketReturnedMailBatch(
  packetIds: string[],
  payload: { note?: string },
  statusElement: HTMLElement,
  lane: HTMLElement | undefined,
  actions: DashboardActionHost
): Promise<void> {
  if (packetIds.length === 0) {
    statusElement.textContent = "Returned mail batch cancelled; no visible sent packets are linked.";
    return;
  }
  statusElement.textContent = `Recording ${formatInteger(packetIds.length)} visible returned mail packets...`;
  try {
    const response = await authenticatedFetch(leadFactoryContactLetterPacketReturnedMailBatchUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...payload, packetIds })
    });
    const body = (await response.json()) as {
      error?: string;
      batchId?: string;
      returnedPacketCount?: number;
      failureCount?: number;
      artifactUrl?: string;
      returned?: Array<{ packetId?: string }>;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    for (const packet of body.returned ?? []) {
      const packetId = packet.packetId?.trim();
      if (!packetId) continue;
      const row = lane?.querySelector<HTMLElement>(`[data-contact-letter-packet-row][data-letter-packet-id="${cssEscape(packetId)}"]`);
      if (row) row.dataset.letterPacketStatus = "returned_mail";
    }
    statusElement.textContent = `Returned mail batch ${body.batchId ?? ""}: ${formatInteger(body.returnedPacketCount ?? 0)} packets returned, ${formatInteger(body.failureCount ?? 0)} failures.`;
    if (body.artifactUrl && body.batchId) {
      await downloadAuthenticatedFile(body.artifactUrl, `${body.batchId}.md`, "text/markdown", statusElement, actions);
    }
  } catch (error) {
    statusElement.textContent = error instanceof Error ? error.message : "Returned mail batch failed.";
  }
}

